/**
 * Regras puras do "Últimas atualizações" (§7.15): quais eventos aparecem, em que ordem, que ícone cada um leva, para
 * onde o item clicável navega e que UF ele destaca no mapa (F104, §9.11.4). Sem Preact: os componentes só desenham.
 */
import type { Geography } from '../../contracts/static';
import { PLACES } from '../../format/places';
import { epochMs, T_OPEN, tMinOfIso } from '../../model/time';
import type {
  Candidate,
  ElectionEvent,
  PlaceCode,
  PlaceRoute,
  ResultsView,
  RouteOffice,
  UfCode,
} from '../../model/types';
import { ufOfMunicipality } from '../../state/actions';

/** Paginação local (§7.15 bloco 3): 16 de cada vez. */
export const FEED_PAGE = 16;

/**
 * Nunca no feed (§7.15): linha do tempo e TV (`results_open`, `count_milestone`); natureza `ops` (status e aviso); e os
 * tipos de NON_BROADCAST_EVENT_TYPES do contrato (`insight_state`, DR-33: o cartão muda de estado no painel, sem
 * notificação). A camada não importa valor de contracts/: o teste de updates confere esta lista contra a constante.
 */
export const NEVER_IN_FEED: ReadonlySet<ElectionEvent['type']> = new Set([
  'results_open',
  'count_milestone',
  'tse_stalled',
  'tse_resumed',
  'correction',
  'insight_state',
]);

/** Evento da camada de projeção (`model_*`, §13.5.3). */
export function isModelEvent(ev: Pick<ElectionEvent, 'type' | 'nature'>): boolean {
  return ev.nature === 'model' || ev.type.startsWith('model_');
}

export interface FeedFilter {
  /** `features.probability`: sem ela, nenhum `model_*` (§7.15; AC-7.15-09). */
  probabilityOn: boolean;
  /** `config.election_date` (relógio de §10.1.1). */
  electionDate: string;
}

/**
 * Os eventos que o feed exibe (§7.15 "Tipos de evento exibidos"): fora os de `NEVER_IN_FEED`; `model_*` só com a flag
 * de probabilidade e nunca com fato ou confirmação antes das 17h00 (E01; `deriveEvents` não aplica a trava, decisão de
 * 09/10 em docs/DECISOES.md).
 */
export function isFeedEvent(ev: ElectionEvent, f: FeedFilter): boolean {
  if (NEVER_IN_FEED.has(ev.type)) return false;
  if (!isModelEvent(ev)) return true;
  if (!f.probabilityOn) return false;
  const epoch = epochMs(f.electionDate);
  return tMinOfIso(ev.occurred_at, epoch) >= T_OPEN && tMinOfIso(ev.detected_at, epoch) >= T_OPEN;
}

/** Ordem (§7.15 bloco 3): `occurred_at` decrescente; empate por `seq` e saliência decrescentes; por fim, o `id`. */
export function compareEvents(a: ElectionEvent, b: ElectionEvent): number {
  const ta = Date.parse(a.occurred_at);
  const tb = Date.parse(b.occurred_at);
  if (ta !== tb) return tb - ta;
  if (a.seq !== b.seq) return b.seq - a.seq;
  if (a.salience !== b.salience) return b.salience - a.salience;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Filtra e ordena (cópia; a lista da vista é congelada). Ids repetidos ficam com a 1ª ocorrência (chave estável). */
export function feedEvents(events: readonly ElectionEvent[], f: FeedFilter): ElectionEvent[] {
  const seen = new Set<string>();
  const out: ElectionEvent[] = [];
  for (const ev of events) {
    if (seen.has(ev.id) || !isFeedEvent(ev, f)) continue;
    seen.add(ev.id);
    out.push(ev);
  }
  return out.sort(compareEvents);
}

// ── Lugar do evento (§7.15 bloco 5; §5.5 E8) ────────────────────────────────────────────────────────────────────
const isUf = (code: string | undefined): code is UfCode =>
  code !== undefined && code !== 'ZZ' && Object.hasOwn(PLACES, code);

/** Para onde o item clicável leva e que lugar ele destaca no mapa. */
export interface EventTarget {
  office: RouteOffice;
  place: PlaceRoute;
  /** UF ou 'ZZ' (o chip do Exterior acende, §9.11.4). */
  link: PlaceCode;
}

const ufTarget = (office: RouteOffice, uf: UfCode): EventTarget => ({
  office,
  place: { kind: 'uf', uf },
  link: uf,
});
const ABROAD: EventTarget = { office: 'president', place: { kind: 'abroad' }, link: 'ZZ' };

/** Destinos que existem nesta configuração (`features.abroad`, `features.governors`); ausente = os dois existem. */
export interface TargetFlags {
  abroad: boolean;
  governors: boolean;
}

/**
 * Tabela de §7.15: Exterior → `#/presidente/exterior`; `uf_complete` e `uf_lead_change` → a UF; `uf_first_votes` só
 * com 1 UF; `capital_complete` → a UF do município; definição e resultado de governador → a UF na aba Governadores.
 * Lotes, viradas nacionais, definições nacionais e `model_*` não navegam (null). Destino desligado pela flag (Exterior
 * sem `features.abroad`, aba Governadores sem `features.governors`) também não: a rota seria canonizada de volta e o
 * botão não faria nada (o mesmo critério da linha do Exterior em "Por região", §7.12).
 */
export function eventTarget(
  ev: ElectionEvent,
  geo: Geography | null,
  flags: TargetFlags = { abroad: true, governors: true },
): EventTarget | null {
  const t = rawTarget(ev, geo);
  if (!t) return null;
  if (t.link === 'ZZ' && !flags.abroad) return null;
  if (t.office === 'governor' && !flags.governors) return null;
  return t;
}

function rawTarget(ev: ElectionEvent, geo: Geography | null): EventTarget | null {
  const d = ev.data;
  switch (d.type) {
    case 'abroad_first_votes':
    case 'abroad_half':
    case 'abroad_complete':
      return ABROAD;
    case 'uf_complete':
    case 'uf_lead_change':
      return isUf(ev.scope.code) ? ufTarget('president', ev.scope.code) : null;
    case 'uf_first_votes': {
      const only = d.places.length === 1 ? d.places[0] : undefined;
      return isUf(only) ? ufTarget('president', only) : null;
    }
    case 'capital_complete': {
      const ibge = d.ibge ?? ev.scope.code;
      const uf = ibge ? ufOfMunicipality(ibge, geo) : null;
      return uf ? ufTarget('president', uf) : null;
    }
    case 'math_decision':
    case 'tse_result':
      return ev.office === 'governor' && isUf(ev.scope.code) ? ufTarget('governor', ev.scope.code) : null;
    default:
      return null;
  }
}

// ── Ícone (§7.15 tabela de tipos) ───────────────────────────────────────────────────────────────────────────────
export type EventIcon =
  | { kind: 'portrait'; candidate: Candidate }
  | { kind: 'ring'; fraction: number }
  | { kind: 'globe' }
  | { kind: 'none' };

const NONE: EventIcon = { kind: 'none' };
const GLOBE: EventIcon = { kind: 'globe' };

function personOf(pair: readonly Candidate[] | undefined, num: string | null | undefined): EventIcon | null {
  const c = num == null ? undefined : pair?.find((x) => x.number === num);
  return c ? { kind: 'portrait', candidate: c } : null;
}

/**
 * Retrato de quem o texto põe em destaque ({X}: líder, vencedor ou quem passou à frente); anel no lote de seções;
 * globo nos primeiros votos do Exterior (e no empate fora do país); `model_*` e `uf_first_votes` sem ícone (nunca
 * retrato na projeção, §7.15).
 */
export function eventIcon(ev: ElectionEvent, view: Pick<ResultsView, 'candidates' | 'governor'>): EventIcon {
  const d = ev.data;
  const pair = view.candidates;
  switch (d.type) {
    case 'sections_batch':
      return { kind: 'ring', fraction: d.national_sections_fraction };
    case 'abroad_first_votes':
      return GLOBE;
    case 'abroad_half':
    case 'abroad_complete':
      return personOf(pair, d.leader) ?? GLOBE;
    case 'uf_complete':
    case 'capital_complete':
      return personOf(pair, d.leader) ?? NONE;
    case 'national_lead_change':
    case 'uf_lead_change':
      return personOf(pair, d.to) ?? NONE;
    case 'math_decision':
    case 'tse_result': {
      if (ev.office === 'president') return personOf(pair, d.winner) ?? NONE;
      const uf = ev.scope.code;
      const gov = uf && isUf(uf) ? view.governor[uf]?.candidates : undefined;
      return personOf(gov, d.winner) ?? NONE;
    }
    default:
      return NONE;
  }
}

// ── Selos do lote (§7.15, AC-7.15-03) ───────────────────────────────────────────────────────────────────────────
/** Com mais de 4 lugares: 3 selos + "+{N}"; até 4, todos. A ordem é a do dado (Δ decrescente). */
export function batchSeals(places: readonly string[]): { shown: string[]; more: number } {
  if (places.length <= 4) return { shown: [...places], more: 0 };
  return { shown: places.slice(0, 3), more: places.length - 3 };
}
